import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for number validation
 */
export interface NumberValidatorOptions extends BaseValidatorOptions {
  min?: number;
  max?: number;
}

/**
 * Validate a number value
 * @param value - The string representation of a number
 * @param options - Validation options (min, max)
 * @returns Validation result with the parsed number value and optional error, or null if invalid
 *
 * @example
 * ```typescript
 * const result = validateNumber("42.5", { min: 0, max: 100 });
 * if (result) {
 *   console.log(result.value); // 42.5
 * }
 * ```
 */
export function validateNumber(value: string, options: NumberValidatorOptions = {}): ValidationResult<number> | null {
  const { field = "value", min, max } = options;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return null;
  }

  if (min !== undefined && numValue < min) {
    return {
      value: numValue,
      error: {
        field,
        message: `"${field}" must be at least ${min} (got ${numValue})`,
        severity: "warning",
      },
    };
  }

  if (max !== undefined && numValue > max) {
    return {
      value: numValue,
      error: {
        field,
        message: `"${field}" must be at most ${max} (got ${numValue})`,
        severity: "warning",
      },
    };
  }

  return { value: numValue };
}
